// API contract between the Deskworks Control Plane, the web app and tests.
// All JSON on the wire is camelCase; timestamps are ISO-8601 strings.
// ---------------------------------------------------------------- enums
export const WORK_ITEM_STATUSES = [
    'created',
    'ready',
    'working',
    'waiting',
    'blocked',
    'needs_approval',
    'complete',
    'failed',
    'cancelled',
];
export const OPEN_WORK_ITEM_STATUSES = [
    'created',
    'ready',
    'working',
    'waiting',
    'blocked',
    'needs_approval',
];
export const TERMINAL_WORK_ITEM_STATUSES = ['complete', 'failed', 'cancelled'];
/** Work Items queue by priority, highest first, from 0 to 100. One created without a priority gets 50 ("normal"). */
export const DEFAULT_WORK_ITEM_PRIORITY = 50;
export const MAX_WORK_ITEM_PRIORITY = 100;
export const EXECUTION_STATUSES = [
    'queued',
    'starting',
    'running',
    'waiting',
    'completed',
    'failed',
    'cancelled',
    'lost',
];
export const ACTIVE_EXECUTION_STATUSES = ['queued', 'starting', 'running'];
/**
 * `lead`: the Lead's own run on a Work Item, woken by an event (docs/features/self-organizing-desks.md). `desk_evaluation`:
 * the Lead's run without a Work Item before migration 0030 (history only).
 */
export const EXECUTION_KINDS = ['work_item', 'desk_evaluation', 'conversation', 'follow_up', 'lead'];
/**
 * `review`: the Desk's approver agent decides whether a proposed item starts. `watch`: the desk's standing item
 * ("Check your desk") where triggers, the heartbeat and "Run now" wake the Lead; it never finishes. `report`: one
 * round of the desk's report: the Lead reports, people answer in its thread, the Lead improves (docs/features/desk-reports.md).
 * `training`: while the desk is learning, its Lead trains it here step by step (docs/features/desk-training.md).
 */
export const WORK_ITEM_KINDS = ['task', 'review', 'watch', 'report', 'training'];
export const REVIEW_DECISIONS = ['approved', 'changes_requested'];
export const RUNTIME_TYPES = [
    'openai_api',
    'anthropic_api',
    'claude_subscription',
    'codex_subscription',
    'local',
];
/** Runtime types the V0 worker can actually execute. */
/** Built-in default model per runtime type (used when neither agent nor profile set one). */
export const DEFAULT_MODELS = {
    anthropic_api: 'claude-opus-5-5',
    openai_api: 'gpt-5',
};
/** Model suggestions per runtime type for pickers (free text stays allowed). */
export const MODEL_SUGGESTIONS = {
    anthropic_api: ['claude-opus-5-5', 'claude-sonnet-5-5', 'claude-haiku-4-5', 'claude-fable-5-1'],
    claude_subscription: ['claude-opus-5-5', 'claude-sonnet-5-5', 'claude-haiku-4-5'],
    openai_api: ['gpt-5'],
    codex_subscription: ['gpt-5'],
};
export const IMPLEMENTED_RUNTIME_TYPES = ['openai_api', 'anthropic_api', 'claude_subscription', 'codex_subscription', 'local'];
/** Runtime types that are connected by logging in (instead of pasting an API key). */
export const SUBSCRIPTION_RUNTIME_TYPES = ['claude_subscription', 'codex_subscription'];
/**
 * The default order for agents on "Automatic" (docs/features/agents-and-runtimes.md): subscriptions first
 * (already paid), then API keys, the demo runtime only when nothing else is connected. A new runtime gets its
 * priority from it (`defaultRuntimePriority`); people change it per runtime afterwards.
 */
export const AUTOMATIC_RUNTIME_ORDER = ['claude_subscription', 'codex_subscription', 'anthropic_api', 'openai_api', 'local'];
/** Priority of a new runtime of this type: 10, 20, … in AUTOMATIC_RUNTIME_ORDER (lowest is tried first). */
export function defaultRuntimePriority(type) {
    const i = AUTOMATIC_RUNTIME_ORDER.indexOf(type);
    return (i < 0 ? AUTOMATIC_RUNTIME_ORDER.length : i) * 10 + 10;
}
export const RESOURCE_CLASSES = ['standard', 'coding'];
export const TRIGGER_TYPES = ['manual', 'cron', 'webhook', 'polling'];
export const DESK_STATUSES = ['active', 'paused', 'archived'];
/**
 * Who approves top-level Work Items that an agent or the system created before
 * they start: a person (`manual`), nobody (`auto`) or the Desk's approver agent.
 */
export const WORK_ITEM_APPROVAL_MODES = ['manual', 'auto', 'agent'];
/**
 * How a Desk's agents handle uncertainty while working: `ask` a person (the Work
 * Item waits under "Needs you") or `god` mode — never stop, decide themselves,
 * approval requests are granted automatically.
 */
export const AGENT_MODES = ['ask', 'god'];
/**
 * Where a desk stands (docs/features/desk-stages.md): `learning` (it learns the work — asks, writes playbooks and
 * skills), `copilot` (it does the work, a person approves each result) or `autopilot` (it works on its own and asks
 * only about what matters). God mode (`agentMode`) is a switch of its own.
 */
export const DESK_STAGES = ['learning', 'copilot', 'autopilot'];
/** Approval type the Lead requests in the Co-Pilot stage before it closes a Work Item. Never auto-approved, not even in God mode. */
export const RESULT_APPROVAL = 'result';
/** The Lead proposes the desk's next stage (docs/features/desk-training.md); a person picks it. Holds nothing. */
export const STAGE_CHANGE_APPROVAL = 'stage_change';
/** What the Lead assumed in place of people while training in God mode, for a person to confirm or correct. Holds nothing. */
export const ASSUMPTIONS_APPROVAL = 'assumptions';
/**
 * Something only a person can do (top up a balance, sign in, give access), asked by the Lead with ask_person
 * (docs/features/agent-mode-and-approvals.md#asking-a-person-to-do-something). Never decided by the Lead, not even in
 * God mode; holds nothing. Closed by the person ("Done" / "Can't"), or by the Lead once it checked it is done.
 */
export const PERSON_TASK_APPROVAL = 'person_task';
/** What showed the desk something it learned (docs/features/desk-training.md#learning-log). */
export const LEARNED_FROM = ['case', 'answer', 'correction', 'assumption', 'observation', 'report'];
/**
 * Cards in the training chat (conversation message `metadata.event`): the Lead proposes the next stage, God mode, or to
 * end the session (its summary is the card's `reason`); a person decides with one click
 * (`POST /desks/:id/training/proposal`); `decided` is set on the card once decided.
 */
export const TRAINING_PROPOSAL_EVENTS = ['stage_proposed', 'god_mode_proposed', 'session_end_proposed'];
/** Why a question to a person on an Autopilot desk is worth asking (docs/features/desk-stages.md). */
export const IMPORTANT_QUESTION_REASONS = ['irreversible', 'not_covered', 'conflict', 'high_stakes', 'guidelines'];
/**
 * Whether the desk's chats may write (docs/features/conversations.md#writing-in-a-chat). never: chats only read;
 * off: a person switches it on per chat; on: new chats start writing.
 */
export const CONVERSATION_WRITES = ['never', 'off', 'on'];
/** Approval type that gates the start of a proposed Work Item. */
export const START_WORK_APPROVAL = 'start_work';
/** Approval type an agent requests when it reaches its Work Item's approval rule. Never auto-approved, not even in God mode. */
export const CHECKPOINT_APPROVAL = 'checkpoint';
/** At most this many answers to pick on an approval or a question (docs/features/agent-mode-and-approvals.md#options). */
export const MAX_DECISION_OPTIONS = 4;
export const DESK_AGENT_ROLES = ['lead', 'available'];
export const DEPENDENCY_RELATIONS = ['blocks', 'depends_on', 'related_to'];
export const APPROVAL_STATUSES = ['pending', 'approved', 'rejected', 'cancelled'];
export const CONNECTION_TYPES = ['http_api', 'mcp_server', 'git_repository', 'email', 'gitlab', 'github', 'slack', 'other'];
/** REST APIs Deskworks serves as an MCP server itself: an `mcp_server` connection with `config.adapter` and `config.baseUrl` (docs/features/mcp-adapters.md). */
export const MCP_ADAPTERS = ['gitlab', 'google_search', 'google_search_console', 'google_analytics', 'web_search', 'chatgpt', 'dataforseo', 'kubernetes', 'meta_ads', 'grafana', 'hetzner_cloud', 'aws'];
/**
 * Adapters that sign in only with OAuth at Google: through Deskworks's Google OAuth client (when the installation has
 * one, `/health` `googleOAuth`), or the customer's own one, whose client secret is then the connection's credential.
 */
export const GOOGLE_OAUTH_ADAPTERS = ['google_search_console', 'google_analytics'];
/** Where an OAuth sign-in comes back to, on the web app's address: `<web origin>` + this is the redirect URI (e.g. of a GitLab application). */
export const OAUTH_CALLBACK_PATH = '/api/oauth/mcp/callback';
export const CONNECTION_STATUSES = ['unknown', 'healthy', 'failing', 'disabled'];
// ---------------------------------------------------------------- auth & tenancy
/** Languages of the web app (docs/features/languages.md). */
export const LOCALES = ['en', 'de'];
// ---------------------------------------------------------------- worker images (docs/features/worker-images.md)
export const WORKER_IMAGE_BUILD_STATUSES = ['queued', 'building', 'succeeded', 'failed'];
export const GUEST_FIELDS = ['firstName', 'lastName', 'company', 'email', 'phone'];
/**
 * Built-in adapters whose agents can be limited to entries chosen from what the signed-in account reaches
 * (GET /connections/:id/scope-choices): Search Console sites, GA4 properties.
 */
export const SCOPE_CHOICE_ADAPTERS = ['google_search_console', 'google_analytics'];
// ---------------------------------------------------------------- workspace database (docs/features/workspace-database.md)
/** Column types offered by the database viewer. */
export const DB_COLUMN_TYPES = ['text', 'integer', 'bigint', 'numeric', 'boolean', 'date', 'timestamptz', 'jsonb', 'uuid'];
/**
 * What a desk shows on its card: working (agents run), checking (the Lead or a reviewer runs), needs_you (an approval,
 * an action or a question waits for a person), watching (nothing runs; waits for the next trigger), paused.
 */
export const DESK_PULSE_STATES = ['needs_you', 'working', 'checking', 'watching', 'paused'];
/** Which way a metric should move: up (e.g. visits), down (e.g. open findings), none. */
export const METRIC_DIRECTIONS = ['up', 'down', 'none'];
/** The time windows of a desk's activity stream: 24 hours, 7 days (6-hour buckets), 30 days (daily buckets). */
export const ACTIVITY_RANGES = ['24h', '7d', '30d'];
// ---------------------------------------------------------------- programs (docs/features/programs.md)
export const PROGRAM_PHASES = ['clarify', 'vision', 'roadmap', 'build', 'prove', 'handover', 'done'];
// ---------------------------------------------------------------- reports (docs/features/desk-reports.md)
export const REPORT_INTERVALS = ['daily', 'weekly', 'monthly'];
/** reporting: the Lead writes it · feedback: people answer · improving: the Lead improves the desk · done. */
export const REPORT_PHASES = ['reporting', 'feedback', 'improving', 'done'];
/** Where a goal stands, in the Lead's judgement. */
export const GOAL_STATUSES = ['on_track', 'at_risk', 'off_track', 'no_data'];
/** helped / no_effect / worse: the next report's verdict on a change; pending until then. */
export const IMPROVEMENT_VERDICTS = ['pending', 'helped', 'no_effect', 'worse'];
export const ASSET_VISIBILITIES = ['private', 'public'];
/** A person approves a Work Item's plan before its runs start (docs/features/work-items.md#the-plan). */
export const PLAN_APPROVAL = 'plan';
export const FINDING_CONFIDENCES = ['confirmed', 'likely', 'derived', 'unchecked'];
export const RUN_SECRET_KINDS = ['text', 'key_value', 'json'];
export const DEFAULT_WORK_ITEM_LABEL = { singular: 'Work Item', plural: 'Work Items' };
// ---------------------------------------------------------------- realtime
/** Socket.IO events pushed by the Control Plane on namespace `/realtime`. */
export const REALTIME_EVENTS = [
    'work_item.updated',
    'work_item.message',
    'work_item.activity',
    'work_item.transcript',
    // Session steps of a run without a Work Item (a chat or a Lead evaluation).
    'session.transcript',
    'agent.started',
    'agent.waiting',
    'agent.completed',
    'approval.requested',
    'approval.resolved',
    /** An action was asked for, decided or finished: data Action. */
    'action.updated',
    'execution.updated',
    'execution.failed',
    'desk.updated',
    'conversation.message',
    'conversation.updated',
    /** A view got a new live or proposed version, or its status changed: data { viewId }. */
    'view.updated',
    /** Rows or structure of the workspace database changed: data { tables: string[], schema: boolean }. */
    'workspace_db.changed',
    /** A desk's files changed (main or a Work Item's copy): data { workItemId, status }. */
    'desk.files',
];
// ---------------------------------------------------------------- actions and desk permissions (docs/features/actions-and-permissions.md)
/** What a desk lets its agents do with an MCP tool: run it, ask a person first, or not at all. */
export const TOOL_PERMISSIONS = ['allow', 'ask', 'deny'];
export const ACTION_STATUSES = ['pending_approval', 'approved', 'running', 'succeeded', 'failed', 'rejected', 'cancelled'];
// ---------------------------------------------------------------- setup assistant
/** What a setup draft can change (docs/features/setup-assistant.md §3). */
export const SETUP_CHANGE_KINDS = [
    'create_agent',
    'update_agent',
    'create_desk',
    'update_desk',
    'set_desk_goals',
    'update_playbook',
    'add_desk_agent',
    'attach_desk_connection',
    'create_trigger',
    'create_work_item',
    'run_desk',
    'create_table',
    'create_view',
    'create_work_item_type',
    'attach_resource',
    'create_skill',
    'update_skill',
    'attach_skill',
    'share_connection',
];
// ---------------------------------------------------------------- desk store
/** Format of a desk template (docs/features/desk-store.md). */
export const DESK_TEMPLATE_FORMAT = 'deskworks.desk/1';
/** Format of a Desk Store entry: one or more desk templates, installed together (docs/features/desk-store.md). */
export const DESK_STORE_FORMAT = 'deskworks.store/1';
// ---------------------------------------------------------------- Front Desk and VICKI (docs/features/front-desk.md)
/**
 * What VICKI may do in the account. `read`: she reads everything; every change she wants waits for a person as an
 * approval card. `read_write`: she changes the account and writes through every connection without asking.
 */
export const VICKI_RIGHTS = ['read', 'read_write'];
/**
 * Who starts. `guided`: she does what people tell her in a chat; her heartbeat and round look, report and propose.
 * `god`: she starts on her own: every heartbeat she gets to know the business, picks what brings her goals closer and
 * does it (as far as her rights go), and reports afterwards.
 */
export const VICKI_MODES = ['guided', 'god'];
/** Whom a chat's mission waits for (docs/features/front-desk.md#driving-a-chat). */
export const VICKI_WAITING_ON = ['vicki', 'desks', 'people'];
/** What VICKI may watch on the event bus (docs/features/front-desk.md#watching). */
export const VICKI_WATCH_EVENTS = ['table_changed', 'work_item_changed', 'approval_requested', 'run_failed', 'files_changed', 'view_changed'];
export const VICKI_MEMORY_KINDS = ['preference', 'fact', 'constraint', 'decision', 'skill'];
export const VICKI_TOPICS = ['business', 'goals', 'customers', 'competition', 'market', 'products', 'people', 'systems', 'work'];
//# sourceMappingURL=index.js.map